import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, FilePlus2Icon, MailIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { StageBadge } from '../components/StageBadge';
import { DetailFieldSection } from '../components/DetailFieldSection';
import { NewLoanModal } from '../components/NewLoanModal';
import { UrlaPanel } from '../components/UrlaPanel';
import { useLeads } from '../contexts/LeadsContext';
import { buildContactFields } from '../data/leadDetails';
import type { DetailField } from '../data/loanDetails';
import { formatAmount } from '../data/mockData';
const tabs = ['Overview', '1003'] as const;
type Tab = (typeof tabs)[number];
function Field({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-759" data-mp="2qn9ja64ppmsz">
      <dt className="text-[11px] uppercase tracking-wide text-gray-400" data-id="element-760" data-mp="2qlgekwdntcnm">{label}</dt>
      <dd className="mt-0.5 text-[13px] text-ink" data-id="element-761" data-mp="2unbouqdkj0dj">{value}</dd>
    </div>;
}
export function LeadDetail() {
  const {
    leadId
  } = useParams();
  const navigate = useNavigate();
  const {
    leads
  } = useLeads();
  const [tab, setTab] = useState<Tab>('Overview');
  const [newLoanOpen, setNewLoanOpen] = useState(false);
  const lead = leads.find(item => item.id === leadId);
  const [contact, setContact] = useState<{
    left: DetailField[];
    right: DetailField[];
  } | undefined>(() => lead ? buildContactFields(lead) : undefined);
  const [savedLabel, setSavedLabel] = useState<string | null>(null);
  useEffect(() => {
    setContact(lead ? buildContactFields(lead) : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);
  useEffect(() => {
    if (!savedLabel) return;
    const timer = window.setTimeout(() => setSavedLabel(null), 2000);
    return () => window.clearTimeout(timer);
  }, [savedLabel]);
  const editContactField = (label: string, value: string) => {
    setContact(prev => {
      if (!prev) return prev;
      const apply = (fields: DetailField[]) => fields.map(field => field.label === label ? {
        ...field,
        value: value || undefined
      } : field);
      return {
        left: apply(prev.left),
        right: apply(prev.right)
      };
    });
    setSavedLabel(label);
  };
  if (!lead) {
    return <div className="rounded-xl border border-gray-200 bg-white px-5 py-12 text-center" data-id="element-762" data-mp="1eadu962s138x">
        <p className="text-[14px] font-semibold text-ink" data-id="element-763" data-mp="1j98bi3l3ijqx">Lead not found</p>
        <p className="mt-1 text-[12.5px] text-gray-500" data-id="element-764" data-mp="26y1dagoefltg">
          This lead may have been converted or reassigned.
        </p>
        <Link to="/leads" className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-brand hover:text-brand-dark" data-id="element-765" data-mp="2bxw4klc36df1">
          <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-766" data-mp="224oy0x5m0rdd" />
          Back to leads
        </Link>
      </div>;
  }
  return <div className="space-y-4" data-id="element-767" data-mp="2ptrc5v11c234">
      <Link to="/leads" className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-gray-500 transition-colors hover:text-ink" data-id="element-768" data-mp="2umst08ksybpc">
        <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-769" data-mp="j4z6h2u5zxxr" />
        Leads
      </Link>

      <header className="rounded-xl border border-gray-200 bg-white" data-id="element-770" data-mp="e6ef7sww4olg">
        <div className="flex flex-wrap items-start gap-4 px-5 py-4" data-id="element-771" data-mp="rz44c67pu9ms">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy text-[14px] font-bold text-white" data-id="element-772" data-mp="nxsatplq149b">
            {lead.name.split(' ').map(part => part[0]).slice(0, 2).join('')}
          </span>
          <div className="min-w-0" data-id="element-773" data-mp="99nrcjeugoph">
            <div className="flex flex-wrap items-center gap-2" data-id="element-774" data-mp="9apbrehr2njv">
              <h1 className="text-[22px] font-bold text-ink" data-id="element-775" data-mp="49rwpas1e9ml">{lead.name}</h1>
              <StageBadge stage={lead.stage} data-id="element-776" data-mp="1kob3n7ezv393" />
            </div>
            <p className="mt-1 text-[13px] text-gray-500" data-id="element-777" data-mp="1kmgf7j4ikt09">
              {lead.state} · {formatAmount(lead.amount)} ·{' '}
              {lead.loanPurpose ?? 'Purpose not captured'}
            </p>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2" data-id="element-778" data-mp="2b1hbsqgqtcf4">
            <button type="button" onClick={() => setNewLoanOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-navy px-3 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-navy/90" data-id="element-779" data-mp="2fyzqkxhfylv1">
              <FilePlus2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-780" data-mp="2tz0pw8i25l8e" />
              Create loan
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-brand-dark" data-id="element-781" data-mp="2yw0ueez9efxe">
              <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-782" data-mp="38p82w8paqc1p" />
              Call now
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12.5px] font-medium text-ink transition-colors hover:bg-gray-50" data-id="element-783" data-mp="3hntf7m46cuu1">
              <MessageSquareIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-784" data-mp="11xtnba82bgay" />
              Text
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12.5px] font-medium text-ink transition-colors hover:bg-gray-50" data-id="element-785" data-mp="x0binw12vn8r">
              <MailIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-786" data-mp="1kpmecz3kmrg1" />
              Email
            </button>
          </div>
        </div>

        <div className="flex gap-1 border-t border-gray-200 px-3" role="tablist" data-id="element-787" data-mp="1fxcsbu5kubj1">
          {tabs.map(item => <button key={item} type="button" role="tab" aria-selected={tab === item} onClick={() => setTab(item)} className={['-mb-px border-b-2 px-3 py-2.5 text-[13px] font-medium transition-colors', tab === item ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-ink'].join(' ')} data-id="element-788" data-mp="1az1fdajzjkwl">
              {item}
            </button>)}
        </div>
      </header>

      {tab === 'Overview' && contact && <div className="space-y-4" data-id="element-789" data-mp="3t39asit9ok1s">
          <DetailFieldSection title="Contact Information" left={contact.left} right={contact.right} onChange={editContactField} data-id="element-790" data-mp="3e7ldzeqa2z7x" />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-id="element-791" data-mp="357oh4ptqiu1s">
            <section className="rounded-xl border border-gray-200 bg-white px-5 py-4" data-id="element-792" data-mp="359hraf3k3wej">
              <h2 className="text-[13px] font-semibold text-ink" data-id="element-793" data-mp="3a6rowi13nedg">Pipeline</h2>
              <dl className="mt-3 grid grid-cols-2 gap-4" data-id="element-794" data-mp="13h38zex3opxl">
                <Field label="Stage" value={lead.stage} data-id="element-795" data-mp="18ev2gdde8k3w" />
                <Field label="Loan purpose" value={lead.loanPurpose ?? '—'} data-id="element-796" data-mp="18emrir0tzovc" />
                <Field label="Amount" value={formatAmount(lead.amount)} data-id="element-797" data-mp="1ik4oo6lx6opb" />
                <Field label="State" value={lead.state} data-id="element-798" data-mp="1dkie64xh7mk7" />
                <Field label="Last touch" value={lead.lastTouch ?? '—'} data-id="element-799" data-mp="1dhvw3bcylonq" />
                <Field label="Call cadence" value={lead.attempts ?? '—'} data-id="element-800" data-mp="1rc6p44vzo4zl" />
              </dl>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white px-5 py-4" data-id="element-801" data-mp="1mjdjlesp4smg">
              <h2 className="text-[13px] font-semibold text-ink" data-id="element-802" data-mp="217zxrd6l7ec3">Next step</h2>
              <p className="mt-2 rounded-lg border border-gray-200 px-3 py-2.5 text-[12.5px] text-gray-600" data-id="element-803" data-mp="1w9exxev8ywo6">
                {lead.detail}
              </p>
            </section>
          </div>

          <p className="h-4 text-[12px] text-success" role="status" data-id="element-804" data-mp="268e8x43hj4ec">
            {savedLabel ? `${savedLabel} saved` : ''}
          </p>
        </div>}

      {tab === '1003' && <UrlaPanel borrower={lead.name} data-id="element-805" data-mp="buwin9bp3tfm" />}

      <NewLoanModal lead={newLoanOpen ? lead : null} onClose={() => setNewLoanOpen(false)} onCreated={() => {
      setNewLoanOpen(false);
      navigate('/loans');
    }} data-id="element-806" data-mp="bvy2o3aovq9h" />
    </div>;
}